import moment from 'moment';
import i18n from 'src/utils/i18n';
import type { InboxRow } from 'src/types/apps/messages';

// Deterministic per-conversation avatar color — matches the design's palette
// (docs/designs/dashboard-match-players-chat-feature). Group rooms always use
// the dark chip.
const AVATAR_COLORS = ['#3F8CFF', '#84BD00', '#FA896B', '#9B6BFF'];
const GROUP_AVATAR_COLOR = '#22242E';

// `stadium_private` rooms are per-(target, stadium) — the same customer/
// player contacted from two different stadiums gets two separate rooms/
// threads, each correctly scoped to its own venue. Only rooms with no
// stadium stamp at all (legacy private 1:1, created before stadium scoping
// existed) stay visible under every venue — same rule the Chats list uses.
// (Reverted: treating all non-`match` rows as always-visible surfaced every
// per-stadium thread for the same person at once as duplicate-looking rows —
// Bugbot: same customer shows twice with different last messages.)
export const inboxRowMatchesStadium = (
  row: Pick<InboxRow, 'stadiumId'>,
  stadiumId?: number | null,
): boolean => stadiumId == null || row.stadiumId == null || row.stadiumId === stadiumId;

export const isUnstartedInboxRow = (
  row: Pick<InboxRow, 'lastMessage' | 'lastMessageImage'>,
): boolean => !row.lastMessage && !row.lastMessageImage;

// Same calculation as mocks/malaeb-player MessageIcon.tsx:
//   for (let key in unreadMessages) count += unreadMessages[key]
// Only positive counts contribute (0 / NaN / missing = not unread).
export const sumUnreadMessages = (
  unreadMessages?: Record<string, number> | null,
): number => {
  if (!unreadMessages) return 0;

  let count = 0;
  for (const key in unreadMessages) {
    count += Number(unreadMessages[key]) || 0;
  }

  return count;
};

export const getRoomUnreadCount = (
  chatId: string,
  unreadMessages?: Record<string, number> | null,
  inboxUnreadCount?: number | null,
): number =>
  Math.max(Number(unreadMessages?.[chatId]) || 0, Number(inboxUnreadCount) || 0);

// Topbar / sidebar badge must follow the same stadium scope as the inbox
// list — otherwise a multi-venue owner sees a count for chats that are not
// in the current inbox (Bugbot: badge ignores stadium filter).
export const sumUnreadMessagesForStadium = (
  unreadMessages: Record<string, number> | null | undefined,
  inbox: InboxRow[],
  stadiumId?: number | null,
): number => {
  // Prefer `unreads_rooms`, but also honor inbox `unread_count` so the
  // sidebar/list still badge when the live unread map is empty or lags.
  const scopedInbox =
    stadiumId == null
      ? inbox
      : inbox.filter((row) => inboxRowMatchesStadium(row, stadiumId));

  if (scopedInbox.length === 0) {
    return stadiumId == null ? sumUnreadMessages(unreadMessages) : 0;
  }

  let count = 0;
  for (const row of scopedInbox) {
    count += getRoomUnreadCount(row.chatId, unreadMessages, row.unreadCount);
  }

  return count;
};

export const pickLocalizedName = (en?: string | null, ar?: string | null): string => {
  const isAr = i18n.language?.startsWith('ar');
  if (isAr && ar) return ar;

  return en || ar || '';
};

export const getInboxRowName = (row: InboxRow): string => {
  if (row.type === 'match') {
    return i18n.t('Chat.groupRoomName', { id: row.chatId.replace('match_', '') });
  }

  return pickLocalizedName(row.targetName, row.targetNameAr);
};

export const getInitials = (name?: string | null): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  
return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const hashSeed = (seed: string): number => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  
return Math.abs(hash);
};

export const getAvatarColor = (seed: string, isGroup = false): string => {
  if (isGroup) return GROUP_AVATAR_COLOR;
  
return AVATAR_COLORS[hashSeed(seed) % AVATAR_COLORS.length];
};

export const formatRelativeTime = (ms: number): string => {
  if (!ms) return '';
  const value = moment(ms).locale(i18n.language);
  const now = moment();

  if (value.isSame(now, 'day')) {
    return value.format('hh:mm A');
  }
  if (value.isSame(now.clone().subtract(1, 'day'), 'day')) {
    return i18n.t('Chat.yesterday');
  }
  if (value.isSame(now, 'week')) {
    return value.format('ddd');
  }
  
return value.format('DD/MM/YYYY');
};

// Label for the centered day separator inside the chat message list —
// coarser granularity than formatRelativeTime (no time-of-day).
export const formatDayLabel = (ms: number): string => {
  if (!ms) return '';
  const value = moment(ms).locale(i18n.language);
  const now = moment();

  if (value.isSame(now, 'day')) {
    return i18n.t('Chat.today');
  }
  if (value.isSame(now.clone().subtract(1, 'day'), 'day')) {
    return i18n.t('Chat.yesterday');
  }
  
return value.format('DD MMM YYYY');
};

// Conversation header — last message clock, e.g. "Aug 02, 01:30 PM".
export const formatHeaderMessageTime = (ms: number): string => {
  if (!ms) return '';

  return moment(ms).locale(i18n.language).format('MMM DD, hh:mm A');
};
